const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const CHALLENGE_TYPES = ['challenge', 'quiz'];
export const OPINION_TYPES = ['hot_take', 'prediction'];

export const DEFAULT_DAY_CONFIG = {
  missions_group_fe: 90,
  challenge_group_fe: 100,
  opinions_group_fe: 60,
  completion_bonus_fe: 100,
};

export const DEFAULT_MILESTONES = [
  {
    days_required: 3,
    fan_energy_reward: 100,
    title: '3 days complete',
    message: 'Nice work — keep showing up',
    sort_order: 1,
    is_active: true,
  },
  {
    days_required: 5,
    fan_energy_reward: 150,
    title: '5 days complete',
    message: 'You are on a roll',
    sort_order: 2,
    is_active: true,
  },
  {
    days_required: 7,
    fan_energy_reward: 250,
    title: '7 days complete',
    message: 'Full cycle',
    sort_order: 3,
    is_active: true,
  },
];

export const DAILIES_FLAG_KEYS = [
  { key: 'dailies', label: 'Daily Tasks page', help: 'Whole Daily Tasks page' },
  { key: 'dailies_bonus', label: 'Bonus track', help: 'Bonus indicator / track card' },
  { key: 'dailies_progress', label: 'Progress', help: 'Progress bar + FE earned/available' },
  { key: 'dailies_tasks', label: 'Task groups', help: 'Task groups on the page' },
  { key: 'dailies_leaderboard', label: 'Leaderboard card', help: 'Leaderboard progress card' },
  { key: 'dailies_spin', label: 'Spin wheel', help: 'Spin wheel block' },
  { key: 'daily_missions', label: 'Daily Missions hub', help: 'Existing Daily Missions hub (separate card)' },
];

export function isDayKey(value) {
  if (!DAY_KEY.test(String(value || ''))) return false;
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

/** Format as `Thu, 03 Nov 2026` without shifting the calendar day. */
export function formatIstDate(dayKey) {
  if (!isDayKey(dayKey)) return dayKey || '—';
  const [y, m, d] = dayKey.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return `${WEEKDAYS[date.getUTCDay()]}, ${String(d).padStart(2, '0')} ${MONTHS[m - 1]} ${y}`;
}

export function weekdayLabel(dayKey) {
  if (!isDayKey(dayKey)) return '';
  const [y, m, d] = dayKey.split('-').map(Number);
  return WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
}

export function addDays(dayKey, days) {
  if (!isDayKey(dayKey)) return '';
  const [y, m, d] = dayKey.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return date.toISOString().slice(0, 10);
}

/** Current IST calendar day as YYYY-MM-DD. */
export function istToday() {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  }
}

export function monthBounds(year, monthIndex) {
  const start = `${year}-${String(monthIndex + 1).padStart(2, '0')}-01`;
  const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  const end = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { start, end, lastDay };
}

export function parseDayConfig(payload) {
  const raw = payload?.config || payload || {};
  return {
    day_key: raw.day_key || '',
    missions_group_fe: Number(raw.missions_group_fe ?? DEFAULT_DAY_CONFIG.missions_group_fe),
    challenge_group_fe: Number(raw.challenge_group_fe ?? DEFAULT_DAY_CONFIG.challenge_group_fe),
    opinions_group_fe: Number(raw.opinions_group_fe ?? DEFAULT_DAY_CONFIG.opinions_group_fe),
    completion_bonus_fe: Number(raw.completion_bonus_fe ?? DEFAULT_DAY_CONFIG.completion_bonus_fe),
  };
}

export function dayMaxFe(config) {
  if (!config) return 0;
  return (
    Number(config.missions_group_fe || 0) +
    Number(config.challenge_group_fe || 0) +
    Number(config.opinions_group_fe || 0) +
    Number(config.completion_bonus_fe || 0)
  );
}

export function publishStatus({ is_published, locked }) {
  if (locked) return 'locked';
  if (is_published) return 'published';
  return 'draft';
}

export function filterActivities(activities, types) {
  return (Array.isArray(activities) ? activities : []).filter((a) =>
    types.includes(String(a.activity_type || '').toLowerCase())
  );
}

export function emptyActivityForm(activityType = 'challenge') {
  const isOpinion = OPINION_TYPES.includes(activityType);
  return {
    activity_type: activityType,
    title: '',
    subtitle: '',
    image_url: '',
    sort_order: 1,
    completion_mode: isOpinion ? 'participation' : 'participation',
    target_value: 1,
    action_key: '',
    navigation_link: '',
    ref_type: '',
    ref_id: '',
    fan_energy_reward: 0,
    force_correction: false,
    config: isOpinion
      ? {
          layout: 'prediction_choice',
          section_id: 'sec_daily_predictions',
          section_title: 'Daily Predictions',
          section_position: 2,
          background_color: ['#36048A', '#1B0244'],
          locks_at: '',
          is_locked: false,
          context: { label: '', images: [] },
          options: [{ id: 'opt_a', label: '', image_url: '' }],
        }
      : {
          layout: 'hero_media',
          type: activityType === 'quiz' ? 'quiz' : 'challenge',
          section_id: 'sec_daily_quiz',
          section_title: activityType === 'quiz' ? 'Daily Quiz' : 'Daily Challenge',
          section_position: 1,
          icon_url: '',
          image_url: '',
          background_color: ['#0C5A6E', '#082848'],
          cta_label: 'Enter now',
          subtitle_open: '',
          subtitle_completed: '',
          deeplink: '',
        },
  };
}

export function activityToForm(activity) {
  const base = emptyActivityForm(activity.activity_type || 'challenge');
  return {
    ...base,
    id: activity.id,
    activity_type: activity.activity_type || base.activity_type,
    title: activity.title || '',
    subtitle: activity.subtitle || '',
    image_url: activity.image_url || '',
    sort_order: activity.sort_order ?? 1,
    completion_mode: activity.completion_mode || 'participation',
    target_value: activity.target_value ?? 1,
    action_key: activity.action_key || '',
    navigation_link: activity.navigation_link || '',
    ref_type: activity.ref_type || '',
    ref_id: activity.ref_id || '',
    fan_energy_reward: activity.fan_energy_reward ?? 0,
    force_correction: false,
    config: {
      ...base.config,
      ...(activity.config && typeof activity.config === 'object' ? activity.config : {}),
    },
  };
}

export function buildActivityPayload(form) {
  const payload = {
    activity_type: form.activity_type,
    title: form.title.trim(),
    subtitle: form.subtitle?.trim() || null,
    image_url: form.image_url?.trim() || null,
    sort_order: Number(form.sort_order) || 1,
    completion_mode: form.completion_mode || 'participation',
    target_value:
      form.completion_mode === 'target' ? Math.max(1, Number(form.target_value) || 1) : 1,
    action_key: form.action_key?.trim() || null,
    navigation_link: form.navigation_link?.trim() || null,
    ref_type: form.ref_type?.trim() || null,
    ref_id: form.ref_id?.trim() || null,
    fan_energy_reward: 0,
    config: form.config || {},
  };
  if (form.force_correction) payload.force_correction = true;
  return payload;
}

export function validateActivityForm(form) {
  const errors = [];
  if (!form.title?.trim()) errors.push('Title is required.');
  if (!['challenge', 'quiz', 'hot_take', 'prediction'].includes(form.activity_type)) {
    errors.push('Pick a valid activity type.');
  }
  if (!['participation', 'target'].includes(form.completion_mode)) {
    errors.push('Completion mode must be participation or target.');
  }
  if (form.completion_mode === 'target' && !(Number(form.target_value) >= 1)) {
    errors.push('Target value must be at least 1.');
  }

  const isOpinion = OPINION_TYPES.includes(form.activity_type);
  const isQuiz = form.activity_type === 'quiz';

  if (isOpinion) {
    const options = Array.isArray(form.config?.options) ? form.config.options : [];
    const validOptions = options.filter((o) => o.label?.trim());
    if (validOptions.length < 1) errors.push('Prediction / hot take needs at least one option.');
    if (options.length > 6) errors.push('Soft limit: at most 6 options.');
  }

  if (isQuiz) {
    const deeplink = form.config?.deeplink || form.navigation_link;
    if (!String(deeplink || '').trim()) {
      errors.push('Quiz cards need a deeplink / navigation link.');
    }
  }

  return errors;
}

export function parsePublishChecklist({ dayConfigSaved, mission, activities, allowEmpty }) {
  const challenges = filterActivities(activities, CHALLENGE_TYPES);
  const opinions = filterActivities(activities, OPINION_TYPES);
  const hasMission = !!(mission && (mission.tasks?.length || mission.id));
  const hasAnyGroup = hasMission || challenges.length > 0 || opinions.length > 0;

  const items = [
    {
      id: 'day_config',
      label: 'Day-config saved',
      ok: !!dayConfigSaved,
    },
    {
      id: 'groups',
      label: allowEmpty
        ? 'Empty day confirmed (or at least one group)'
        : 'At least one non-empty group',
      ok: hasAnyGroup || allowEmpty,
    },
  ];

  challenges.forEach((a) => {
    const form = activityToForm(a);
    const errs = validateActivityForm(form);
    items.push({
      id: `challenge_${a.id}`,
      label: `Challenge/quiz “${a.title || a.id}” valid`,
      ok: errs.length === 0,
      detail: errs.join(' '),
    });
  });

  opinions.forEach((a) => {
    const form = activityToForm(a);
    const errs = validateActivityForm(form);
    items.push({
      id: `opinion_${a.id}`,
      label: `Opinion “${a.title || a.id}” valid`,
      ok: errs.length === 0,
      detail: errs.join(' '),
    });
  });

  return items;
}

export function emptyPrize(index = 1) {
  return {
    id: `seg_${index}`,
    label: '',
    image_url: null,
    fan_energy: 0,
    weight: 10,
  };
}

export function weightPercents(prizes) {
  const sum = (prizes || []).reduce((acc, p) => acc + (Number(p.weight) || 0), 0);
  if (!sum) return (prizes || []).map(() => 0);
  return (prizes || []).map((p) => Math.round(((Number(p.weight) || 0) / sum) * 1000) / 10);
}
